import { useCrmEnabled } from "@/lib/outletFeatures";
import { CRM_ENTITLEMENTS } from "@/rbac/screenCatalog";

export { CRM_ENTITLEMENTS };

export type PlanCode = "mini" | "pro" | "max";
export type EntitlementKey =
  | "customMenus"
  | "interactiveQr"
  | "feedbackCustom"
  | "integrations"
  | "basicCrm"
  | "fullCrm"
  | "offers"
  | "loyalty"
  /** CRM features for the outlet (switched per outlet by Dhaba support, not by plan). */
  | "crm";

export type Entitlements = Record<EntitlementKey, boolean>;

const MINI: Entitlements = {
  customMenus: false,
  interactiveQr: false,
  feedbackCustom: false,
  integrations: false,
  basicCrm: false,
  fullCrm: false,
  offers: false,
  loyalty: false,
  crm: true,
};
const PRO: Entitlements = {
  ...MINI,
  customMenus: true,
  interactiveQr: true,
  feedbackCustom: true,
  integrations: true,
  basicCrm: true,
};
const MAX: Entitlements = {
  ...PRO,
  fullCrm: true,
  offers: true,
  loyalty: true,
};
/**
 * CRM switched off for the outlet: customer screens, campaigns, automations
 * and feedback go. The loyalty points program (earn, redeem at billing,
 * Settings > Loyalty) is not CRM and stays on.
 */
const MAX_CRM_OFF: Entitlements = {
  ...MAX,
  crm: false,
  basicCrm: false,
  fullCrm: false,
  offers: false,
};

export interface PlanEntitlementsResult {
  entitlements: Entitlements;
  planCode: PlanCode;
  isLoading: boolean;
  /** True once the real subscription has loaded (vs. the pessimistic mini default). */
  isResolved: boolean;
  /** CRM is switched off for the selected outlet (CRM entitlements are false for that reason, not the plan). */
  crmDisabled: boolean;
}

export function usePlanEntitlements(): PlanEntitlementsResult {
  // The restaurant product is free. Mini/Pro/Max walls are gone; every
  // feature is included. planCode stays "max" so leftover Mini simple-mode
  // branches (custom menus) keep the full UI. CRM can still be switched off
  // per outlet by Dhaba support.
  const crmEnabled = useCrmEnabled();
  return {
    entitlements: crmEnabled ? MAX : MAX_CRM_OFF,
    planCode: "max",
    isLoading: false,
    isResolved: true,
    crmDisabled: !crmEnabled,
  };
}
